import { useTranslations } from "@zapier/i18n";
import { EmptyTableDisplay, useRouterParams } from "@zapier/shared";
import { EmptyZapTableTypes } from "./useShowEmptyZapTable";
import { getCreateZapUrl } from "@zapier/utils";
import { TeamsUpsell } from "./TeamsUpsell/TeamsUpsell";

interface Props {
  tableType: EmptyZapTableTypes;
}

export function EmptyZapTable({ tableType }: Props) {
  const t = useTranslations("empty-zap-table");

  const { clearFilterParams } = useRouterParams();

  /* Empty Search */
  if (tableType === EmptyZapTableTypes.EmptySearchResults) {
    return (
      <EmptyTableDisplay
        actionButtonText={t("actions.clear-search")}
        headerText={t("headings.no-results-found")}
        icon="navSearch"
        onActionButton={() => clearFilterParams()}
        subHeaderText={t("descriptions.adjust-filters-or-clear-search")}
      />
    );
  }

  /* Empty Trash */
  if (tableType === EmptyZapTableTypes.EmptyTrash) {
    return (
      <EmptyTableDisplay
        icon="actionTrash"
        headerText={t("headings.trash-empty")}
        subHeaderText={t("descriptions.trash-retention-policy")}
      />
    );
  }

  /* Empty Pending */
  if (tableType === EmptyZapTableTypes.EmptyPendingApproval) {
    return (
      <EmptyTableDisplay
        icon="formCheckCircleOutlined"
        headerText={t("headings.no-pending-zaps")}
        subHeaderText={t("descriptions.billing-issues-explanation")}
      />
    );
  }

  /* Empty Shared List */
  if (tableType === EmptyZapTableTypes.NoSharedZaps) {
    return (
      <EmptyTableDisplay
        icon="miscBoltOutlined"
        headerText={t("headings.no-shared-zaps")}
        subHeaderText={t("descriptions.sharing-collaboration-benefits")}
      />
    );
  }

  /* No access to shared Zaps or folders */
  if (tableType === EmptyZapTableTypes.UpgradeForSharing) {
    return <TeamsUpsell />;
  }

  /* Single Zap ID in URL and Zap not found */
  if (tableType === EmptyZapTableTypes.ZapNotFound) {
    return (
      <EmptyTableDisplay
        icon="miscBoltOutlined"
        headerText={t("headings.zap-not-found")}
        subHeaderText={t("descriptions.zap-access-denied-or-deleted")}
      />
    );
  }

  return (
    <EmptyTableDisplay
      icon="miscBoltOutlined"
      headerText={t("headings.no-zaps-created")}
      subHeaderText={t("descriptions.build-first-workflow")}
      actionButtonText={t("actions.explore-templates")}
      actionButtonIcon="navExplore"
      actionButtonColor="tertiary"
      actionButtonHref="/app/templates/use-cases"
      secondActionButtonText={t("actions.create-zap")}
      secondActionButtonIcon="formAdd"
      secondActionButtonHref={getCreateZapUrl()}
    />
  );
}
